import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useRegions from './hooks/useRegions';
import useTRSClusters from './hooks/useTRSClusters';
import useCreateTRSCluster from './queries/useCreateTRSCluster';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import {
    breadCrumbTrail,
    ERROR_MESSAGES,
    addHeader as header,
    initialState,
    trsPath,
} from './constants';
import TRSClusterNameField from './formFields/TRSClusterNameField';
import TRSClusterRegionField from './formFields/TRSClusterRegionField';

const TRSClustersAdd = () => {
    const navigate = useNavigate();
    const { darkMode } = useSelector((state) => state.settings);
    const [isDisabled, setIsDisabled] = useState(true);
    const [regionValidationError, setRegionValidationError] = useState('');

    const {
        data: regions = [],
        isLoading: regionsLoading,
        isFetching: regionsFetching,
        isRefetching: regionsRefetching,
        error: regionsError,
    } = useRegions();

    // Fetch existing TRS clusters for validation
    const {
        data: clusters = [],
        isLoading: clustersLoading,
        isFetching: clustersFetching,
        isRefetching: clustersRefetching,
        error: clustersError,
    } = useTRSClusters();

    const usedRegionIds = useMemo(() => {
        if (!clusters.length) return new Set();
        return new Set(clusters.map((cluster) => cluster.regionID));
    }, [clusters]);

    // Check if a region is already used
    const isRegionUsed = (regionId) => {
        return usedRegionIds.has(regionId);
    };

    // Get region name by ID
    const getRegionName = (regionId) => {
        const region = regions.find((r) => r.id === regionId);
        return region?.name || 'Unknown Region';
    };

    const handleSuccess = () => {
        toast.success('Create a TRS Cluster successfully!');
        navigate(trsPath);
    };

    const handleError = (error) => {
        console.error('Failed to create TRS Cluster:', error);
        toast.error('Failed to create a TRS Cluster. Please try again.');
    };

    const { mutate: createCluster, isPending } = useCreateTRSCluster(
        handleSuccess,
        handleError,
    );

    const finalFormSubmit = async (values) => {
        if (!values.name || values.name.length < 2) {
            console.error(ERROR_MESSAGES.CLUSTER_NAME_REQUIRED);
            return;
        }

        if (!values.regionID) {
            console.error(ERROR_MESSAGES.REGION_REQUIRED);
            return;
        }

        // Check if region is already used (extra validation just in case, as options also disabled those regions)
        if (isRegionUsed(values.regionID)) {
            const regionName = getRegionName(values.regionID);
            setRegionValidationError(
                `Only one cluster per region is supported. The region "${regionName}" already has a cluster, so you cannot save a cluster for this region.`,
            );
            return;
        }

        const finalSubmit = {
            name: values.name,
            regionID: values.regionID,
        };

        createCluster(finalSubmit);
    };

    const formFields = (errors, touched, values) => {
        const hasValidationError = !!regionValidationError;
        const hasRequiredFields = values?.name && values?.regionID;

        if (hasRequiredFields && !hasValidationError) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }

        const fields = [
            <TRSClusterNameField
                key="name"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                isDisabled={isDisabled}
            />,
            <TRSClusterRegionField
                key="regionID"
                errors={errors}
                touched={touched}
                values={values}
                darkMode={darkMode}
                regions={regions}
                usedRegionIds={usedRegionIds}
                isDisabled={isDisabled}
            />,
        ];

        return fields;
    };

    const isFetchingData =
        regionsLoading ||
        regionsRefetching ||
        regionsFetching ||
        clustersLoading ||
        clustersRefetching ||
        clustersFetching ||
        isPending;

    // Handle loading errors
    if (regionsError) {
        toast.error('Failed to load regions. Please refresh the page.');
    }

    if (clustersError) {
        toast.error(
            'Failed to load existing clusters. Please refresh the page.',
        );
    }

    if (isFetchingData) {
        return <LoadingIndicator />;
    }

    return (
        <FormBase
            isDisabled={isDisabled}
            breadCrumbTrail={breadCrumbTrail}
            header={header}
            initialValues={initialState}
            finalFormSubmit={finalFormSubmit}
            backLink={[trsPath, { state: { index: 0 } }]}>
            {({ errors, touched, values }) =>
                formFields(errors, touched, values)
            }
        </FormBase>
    );
};

export default TRSClustersAdd;
